import 'project/polyfill'
import Link from 'next/link'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import { I18NConfig } from 'next/dist/server/config-shared'

import {
  TReviews,
  TPlanSummary,
  TBlogPostResult,
} from 'common/cms-types'
import SeoContainer, {
  SeoContainerProps,
} from 'components/util/containers/SeoContainer'
import {
  heroGap,
  marginHeader,
  sectionGap,
  sectionGapSmall,
  sectionPadding,
  sectionPaddingLarge,
} from 'project/padding'
import MailPortalHighlights, {
  saasSolutionsHighlights,
} from 'components/cards/new/common/MailPortalHighlights'
import { MailCountTrust } from 'components/MailCountTrust'
import { DesktopBreak } from 'components/DesktopBreak'
import { getInitialMailCount } from 'common/utils/initialMailCount'

import { Utils } from 'common/utils'
import { Query } from '@prismicio/types'
import { createClient } from '../prismicio'
import { PlanSlug } from 'common/planSlugs'
import Button from 'components/forms/Button'
import HoxtonMixLogo from 'components/icons/HoxtonMixLogo'
import HeroBanner from 'components/hero/HeroBanner'
import { getPosts, getReviews } from 'common/cms-sdk'
import { getPlans } from 'common/planData'
import HeadingAndText from 'components/HeadingAndText'
import { seoStrings } from 'common/strings/seo-strings'
import BlogSection from 'components/sections/BlogSection'
import PartnersCard from 'components/cards/new/PartnersCard'
import FeaturesCard from 'components/cards/new/FeaturesCard'
import AppPromoCard from 'components/cards/new/AppPromoCard'
import WhoAreWeCard from 'components/cards/new/WhoAreWeCard'
import ContactUsCard from 'components/cards/new/ContactUsCard'
import TidePromoCard from 'components/cards/new/TidePromoCard'
import ReviewsSection from 'components/sections/ReviewsSection'
import { NextPageWithLayout } from '../types/nextPageWithLayout'
import SubscriptionPricing from 'components/SubscriptionPricing'
import { homeBreadcrumb } from 'components/util/containers/Breadcrumbs'
import SalesPitchCard from 'components/cards/new/common/SalesPitchCard'
import { ButtonType } from '../types/global'
import PartnersTestimonial from 'components/cards/new/PartnerTestimonialCard'
import EmbededNewsletterForm from 'components/sections/EmbededNewsletterForm'
import VirtualOfficePromoCard from 'components/cards/new/VirtualOfficePromoCard'
import SaaSOfficeSolutionCard from 'components/cards/new/SaaSOfficeSolutionCard'
import VirtualAddressPromoCard from 'components/cards/new/VirtualAddressPromoCard'
import InnovationRecognitionSummary from 'components/cards/new/InnovationRecognitionSummary'
import ConnectWithCustomers from 'components/WhatsApp/ConnectWithCustomers'
import NoSetupFeesSection from 'components/cards/new/NoSetupFeesSection'

export type HomePageType = {
  posts: Query<TBlogPostResult>
  plans: TPlanSummary[]
  reviews: TReviews
}

const HomePage: NextPageWithLayout<HomePageType> = ({
  plans,
  posts,
  reviews,
}) => {
  const router = useRouter()
  const seoProps: SeoContainerProps['seoProps'] = {
    description: seoStrings.indexDescription,
    openGraph: {
      description: seoStrings.indexOGDescription,
      url: 'https://www.hoxtonmix.com/',
    },
    title: seoStrings.indexTitle,
  }
  const planSummary = plans.find((v) => v.slug === PlanSlug.SCAN_LITE)!
  const whatsappPlanSummary = plans.find((v) => v.slug === PlanSlug.PHONE)!
  return (
    <SeoContainer
      alternate={[
        { href: '/au', lang: 'en-au' },
        { href: '/', lang: 'en-gb' },
        { href: '/', lang: 'x-default' },
      ]}
      breadcrumb={homeBreadcrumb()}
      seoProps={seoProps}
    >
      <main
        className={classNames(
          'overflow-x-hidden new-design d-flex flex-column bg-grey6',
          sectionGap,
        )}
      >
        <div className={classNames('d-flex flex-column', heroGap)}>
          <HeroBanner
            logo={
              <HoxtonMixLogo
                className='mb-5 mb-lg-8'
                containerClassName='w-lg-55'
              />
            }
            robotImage={{
              alt: 'HaiRo™ robot mascot displaying mail interface',
              dimensions: {
                height: 1000,
                width: 827,
              },
              src: '/images/new/robot-mail-preview.png',
            }}
            cta={
              <SubscriptionPricing
                hideRegister
                showLink
                planSummary={planSummary}
                buttonType={ButtonType.HOMEPAGE_HERO}
                rightButton={
                  <Link
                    aria-label='Product Tour'
                    className='text-decoration-none'
                    href={'/virtual-office-london-old-street#demo'}
                  >
                    <Button
                      theme={'secondary'}
                      className={classNames(
                        'w-100 text-decoration-none d-none d-xl-block px-12 w-lg-auto mt-5 mt-lg-8',
                        {},
                      )}
                    >
                      Product Tour
                    </Button>
                  </Link>
                }
              />
            }
            title='Want a Virtual Registered Office Address?'
            description={
              <div className={classNames('mb-2')}>
                <div className='fs-fluid-24-32 fw-semibold'>
                  Register your London business address and let our AI agent{' '}
                  <span className='fw-bold color-pink2'>HaiRo™</span> organise,
                  digitise, and deliver your post.
                </div>
              </div>
            }
            subtitle={<></>}
          />
          <div
            className={classNames('container d-flex flex-column', sectionGap)}
          >
            <h2
              className={classNames(
                'fs-fluid-36-80 lh-105 fw-semibold text-center whitespace-nowrap',
                sectionPadding,
              )}
            >
              <span>
                Our
                <span className='color-pink2'> AI-powered </span>
                mail processing
              </span>
              <DesktopBreak />
              <span>agent, HaiRo™, scans your post and</span>
              <DesktopBreak />
              <span>delivers it directly to your inbox.</span>
            </h2>
          </div>
        </div>

        <div
          className={classNames(
            sectionGapSmall,
            'container d-flex flex-column mt-4',
          )}
        >
          <VirtualOfficePromoCard />
          <ConnectWithCustomers planSummary={whatsappPlanSummary} />
          <PartnersCard />
        </div>
        <div className={classNames('container d-flex flex-column', sectionGap)}>
          <div>
            <HeadingAndText
              className={classNames(
                'px-lg-0 px-6 ',
                marginHeader,
                sectionPadding,
              )}
              classesForTitle='fs-fluid-36-80 lh-105'
              subtitleSize='large'
              title={`Our virtual office address service includes everything in <span class="color-pink2">one simple plan</span>.`}
              subtitle={`<h3 class="fw-semibold">Let HaiRo™ take care of your mail and documents.</h3>`}
            />
            <FeaturesCard planSummary={planSummary} />
          </div>
          <div>
            <h2
              className={classNames(
                'fs-fluid-36-80 lh-105 fw-semibold text-center',
                marginHeader,
                sectionPaddingLarge,
              )}
            >
              <MailCountTrust highlightClassName='color-pink2' />
            </h2>
            <div className={marginHeader}>
              <PartnersTestimonial
                tideReview='"Incorporating a registered office address service into our company formation offering means Tide now makes it quicker and easier than ever for people to register a business and get themselves up and running. Having a registered office address allows entrepreneurs to keep their personal data private and run a professional business. As the trend towards working from home seems set to continue, we expect to see increased interest in this service."'
                annaReview='Adding a registered office address to our company formation service makes it faster and easier than ever for entrepreneurs to set up their business and get started. A registered office address helps protect personal information while maintaining a professional image. With the growing trend of working from home, we anticipate even greater demand for this convenient and secure solution.'
              />
            </div>
            <div className={'text-center'}>
              <SubscriptionPricing
                planSummary={planSummary}
                buttonType={ButtonType.HOMEPAGE_TESTIMONIALS_CTA}
              />
            </div>
          </div>
          <div
            className={classNames(
              sectionGapSmall,
              'container d-flex flex-column mt-4',
            )}
          >
            <AppPromoCard planSummary={planSummary} />
            <VirtualAddressPromoCard
              planSummary={planSummary}
            />
          </div>
          <NoSetupFeesSection plans={plans} />
          <div
            className={classNames(
              sectionGapSmall,
              'container d-flex flex-column mt-4',
            )}
          >
            <div className='row gy-5'>
              <div className='col-12 col-lg-6 '>
                <ContactUsCard />
              </div>
              <div className='col-12 col-lg-6'>
                <TidePromoCard planLabel='virtual office plan' />
              </div>
            </div>
            <SaaSOfficeSolutionCard />
          </div>
          <MailPortalHighlights
            headingSize={4}
            title={
              <span className='whitespace-nowrap'>
                SaaS solutions for <br className='d-none d-lg-block' />{' '}
                accountancies and fintech.
              </span>
            }
          >
            {saasSolutionsHighlights.map((highlight, index) => (
              <div className='pb-4' key={index}>
                <span className='fw-semibold'>{highlight.title}</span>
                <p className='pt-4'>{highlight.content}</p>
              </div>
            ))}
            <div className='text-center'>
              <Link
                className='btn btn-lg btn-primary w-100 w-lg-auto mt-2 px-22 mt-lg-13'
                href={'/enterprise'}
              >
                Enterprise Solutions
              </Link>
            </div>
          </MailPortalHighlights>
        </div>
        <BlogSection posts={posts} />
        <div className={classNames('container d-flex flex-column', sectionGap)}>
          <WhoAreWeCard />
          <InnovationRecognitionSummary />
          <EmbededNewsletterForm />
        </div>

        <ReviewsSection data={reviews} pageUrl={router.pathname} />
      </main>
    </SeoContainer>
  )
}
const PAGE_SIZE = 4

export async function getStaticProps(
  context: I18NConfig & { locale?: string; params: { uid: string } },
) {
  try {
    const client = createClient(context)
    const lang = Utils.getLocaleID(context.locale || context.defaultLocale)
    const plans = await getPlans()
    const posts = await getPosts(client, 1, lang, PAGE_SIZE, undefined)
    const reviews = await getReviews()
    return {
      props: {
        plans,
        posts,
        reviews,
      },
    }
  } catch (e) {
    console.log(e)
    return {
      props: {},
    }
  }
}

export default HomePage
